import type { MessageKey } from '@freightapi/i18n'
import { Button, formatDateTime, Spinner } from '@freightapi/ui'
import { createFileRoute } from '@tanstack/react-router'
import { useCallback, useEffect, useRef, useState } from 'react'
import { QueuedNotice } from '../components/queue/QueuedNotice'
import { StatusScreen } from '../components/StatusScreen'
import { HandoffPanel } from '../components/sign/HandoffPanel'
import { PickupDone } from '../components/sign/PickupDone'
import { PickupParties } from '../components/sign/PickupParties'
import { ShipmentSummary } from '../components/sign/ShipmentSummary'
import { SignForm } from '../components/sign/SignForm'
import { ApiError } from '../lib/api'
import { type SignLocale, useSignI18n } from '../lib/locale'
import { newQueueGroup, queueSignature, useSubmissionQueue } from '../lib/offline-queue'
import {
  type HeldLink,
  isPickupComplete,
  isPickupRole,
  type PickupParty,
  type PickupRole,
  partiesOf,
  partyFor,
  type QueuedSignature,
  type SignedBy,
  settleRecorded,
} from '../lib/pickup'
import type { QueueItem } from '../lib/queue-store'
import {
  createHandoff,
  type DrawnSubmission,
  type Handoff,
  loadSignContext,
  type SignContext,
} from '../lib/sign'

/**
 * The pickup session (M2-06): `SIGN_BASE_URL/p/<carrier token>?consignor=<sender token>`.
 *
 * At a loading dock boxes 22 and 23 are filled in within minutes of each other, so this screen
 * keeps both links open at once and lets either role go first. A sign token names exactly one
 * role and the API stores only its hash, so the second link cannot be derived from the first: the
 * party that created both requests puts it in the URL. Without it the session still opens — it
 * then shows the sender's row from `related_requests` and waits for that link's own device.
 *
 * "Own device" is the handoff of M1-10: a second token, ten minutes and one call, in a QR code.
 */
export interface PickupSearch {
  /** The sender's sign link, so both roles can sign in one session. */
  readonly consignor?: string
}

export const Route = createFileRoute('/p/$token')({
  component: PickupScreen,
  validateSearch: (search: Record<string, unknown>): PickupSearch =>
    typeof search.consignor === 'string' && search.consignor !== ''
      ? { consignor: search.consignor }
      : {},
})

/** How often the handoff screen asks whether the other device is done. */
const POLL_INTERVAL_MS = 4000

/** What the session is doing right now; which rows it shows is derived, never stored. */
type PickupView =
  | { readonly step: 'overview' }
  | { readonly step: 'sign'; readonly role: PickupRole; readonly busy: boolean }
  | {
      readonly step: 'handoff'
      readonly role: PickupRole
      readonly handoff?: Handoff
      readonly failed: boolean
    }
  /** Drawn in a yard with no signal: the queue of M2-08 has it and will deliver it. */
  | { readonly step: 'queued'; readonly role: PickupRole; readonly item: QueueItem }

interface PickupSession {
  /** The freshest context this session has; the driver's link is preferred, it sees the most. */
  readonly base: SignContext
  readonly held: readonly HeldLink[]
  /** Signatures the API took while this session was open, for when no link is left to re-read. */
  readonly signedHere: Partial<Record<PickupRole, SignedBy>>
  /** Signatures this session drew without a connection; they are on the queue, not at the API. */
  readonly queuedHere: Partial<Record<PickupRole, QueuedSignature>>
  readonly view: PickupView
}

type PickupState =
  | { readonly kind: 'loading' }
  | { readonly kind: 'expired' }
  | { readonly kind: 'revoked' }
  | { readonly kind: 'used' }
  | { readonly kind: 'not_found' }
  | { readonly kind: 'unusable' }
  | {
      readonly kind: 'error'
      readonly messageKey: MessageKey<'sign'>
      readonly retryable: boolean
    }
  | { readonly kind: 'session'; readonly session: PickupSession }

function PickupScreen() {
  const { token } = Route.useParams()
  const { consignor } = Route.useSearch()
  const { t, locale } = useSignI18n()
  const [state, setState] = useState<PickupState>({ kind: 'loading' })
  const [attempt, setAttempt] = useState(0)

  // Only the `Accept-Language` of the next request, never a reason to fetch again.
  const localeRef = useRef(locale)
  localeRef.current = locale

  /**
   * The session as it stands, readable from a callback that has already awaited something. The
   * screen has one user with one thumb, so there is no update to race; a functional `setState`
   * across every await would only obscure that.
   */
  const sessionRef = useRef<PickupSession | null>(null)
  const setSession = useCallback((next: PickupSession) => {
    sessionRef.current = next
    setState({ kind: 'session', session: next })
  }, [])

  /**
   * One errand per role, not one per session. The two rows are two capabilities with two tokens,
   * and the queue holds everything of one errand behind its earliest item (M2-08): sharing a group
   * would let a sender signature that gets refused strand the carrier's, which has nothing to do
   * with it. A role keeps its group across a retry, so a second attempt is the same errand.
   */
  const groups = useRef(new Map<PickupRole, string>())
  const groupFor = useCallback((role: PickupRole): string => {
    const existing = groups.current.get(role)
    if (existing !== undefined) return existing
    const group = newQueueGroup()
    groups.current.set(role, group)
    return group
  }, [])

  // `attempt` is not read in the body; it is what "retry" increments, and re-running the effect is
  // the entire point of the dependency.
  // biome-ignore lint/correctness/useExhaustiveDependencies: attempt is the retry trigger
  useEffect(() => {
    const controller = new AbortController()
    setState({ kind: 'loading' })
    sessionRef.current = null

    const tokens = consignor === undefined ? [token] : [token, consignor]
    openLinks(tokens, localeRef.current, controller.signal)
      .then((opened) => {
        if (controller.signal.aborted) return
        if (opened.links.length === 0) {
          setState(failureState(opened.failure))
          return
        }
        // The path token is the session; a link of a role that never signs at loading is not one.
        const base = opened.links[0]?.context
        if (base === undefined) return
        setSession({
          base,
          held: opened.links,
          signedHere: {},
          queuedHere: {},
          view: { step: 'overview' },
        })
      })
      .catch((cause: unknown) => {
        if (!controller.signal.aborted) setState(failureState(cause))
      })

    return () => controller.abort()
  }, [token, consignor, attempt, setSession])

  const retry = useCallback(() => setAttempt((value) => value + 1), [])
  const view = state.kind === 'session' ? state.session.view : null

  /** The role whose QR code still has to be fetched; `null` while nothing is being handed off. */
  const creatingFor =
    view?.step === 'handoff' && view.handoff === undefined && !view.failed ? view.role : null

  useEffect(() => {
    const session = sessionRef.current
    if (creatingFor === null || session === null) return
    const link = session.held.find((held) => held.context.role === creatingFor)
    if (link === undefined) return

    const controller = new AbortController()
    const settle = (next: PickupView) => {
      const current = sessionRef.current
      if (!controller.signal.aborted && current !== null) setSession({ ...current, view: next })
    }
    createHandoff(link.token, { locale: localeRef.current, signal: controller.signal })
      .then((handoff) => settle({ step: 'handoff', role: creatingFor, handoff, failed: false }))
      .catch(() => settle({ step: 'handoff', role: creatingFor, failed: true }))

    return () => controller.abort()
  }, [creatingFor, setSession])

  /** The role that is signing on another device right now, and is therefore being waited for. */
  const waitingFor = view?.step === 'handoff' && view.handoff !== undefined ? view.role : null

  useEffect(() => {
    if (waitingFor === null) return
    const timer = setInterval(() => {
      void poll(waitingFor, localeRef.current, sessionRef.current, setSession)
    }, POLL_INTERVAL_MS)
    return () => clearInterval(timer)
  }, [waitingFor, setSession])

  const submit = useCallback(
    (link: HeldLink, role: PickupRole, body: DrawnSubmission) => {
      const before = sessionRef.current
      if (before === null) return
      setSession({ ...before, view: { step: 'sign', role, busy: true } })

      queueSignature({ token: link.token, group: groupFor(role), body })
        .then(async (sent) => {
          const current = sessionRef.current ?? before
          // Either way the link is out of this session's hands: it has been spent, or the queue
          // is going to spend it. Nothing here may offer it again, to this device or another.
          const held = current.held.filter((other) => other.token !== link.token)

          if (sent.state === 'queued') {
            setSession({
              ...current,
              held,
              queuedHere: {
                ...current.queuedHere,
                [role]: { item: sent.item, name: body.signer.name },
              },
              view: { step: 'queued', role, item: sent.item },
            })
            return
          }

          setSession({
            ...current,
            base: await refreshed(current.base, held, localeRef.current),
            held,
            signedHere: {
              ...current.signedHere,
              [role]: {
                name: sent.result.signer.name,
                signed_at: sent.result.evidence.signed_at,
              },
            },
            view: { step: 'overview' },
          })
        })
        // Only a refusal reaches here; anything the queue can still carry was resolved above.
        .catch((cause: unknown) => {
          const failure = failureState(cause)
          const current = sessionRef.current
          // A network hiccup must not cost the signature that was just drawn: stay on the form.
          if (failure.kind === 'error' && failure.retryable && current !== null) {
            setSession({ ...current, view: { step: 'sign', role, busy: false } })
            return
          }
          setState(failure)
        })
    },
    [groupFor, setSession],
  )

  /**
   * What the queue did with the captures this session handed it.
   *
   * The runner deletes an item only after the API answered it, so an id that is gone from a store
   * that has been read is a signature that landed — the same evidence the handoff poll settles for
   * in `poll` below, and the only one left once no link remains to read the note back with. A row
   * that is still there follows the item, so a refusal stops saying "on its way".
   */
  const queue = useSubmissionQueue()

  useEffect(() => {
    const session = sessionRef.current
    if (session === null || !queue.ready) return

    const settled = settleQueued(session, queue.items)
    if (settled === null) return
    setSession(settled.session)
    if (!settled.delivered) return

    // A link that is still held now reads back the real row, name and timestamp and all; with
    // nothing left to ask, the name that was captured is all this session will ever know.
    void refreshed(settled.session.base, settled.session.held, localeRef.current).then((base) => {
      const current = sessionRef.current
      if (current !== null && base !== current.base) setSession({ ...current, base })
    })
  }, [queue, setSession])

  const show = useCallback(
    (next: PickupView) => {
      const current = sessionRef.current
      if (current !== null) setSession({ ...current, view: next })
    },
    [setSession],
  )

  switch (state.kind) {
    case 'loading':
      return (
        <div className="flex justify-center py-16">
          <Spinner label={t('loading')} size="lg" />
        </div>
      )

    case 'expired':
      return (
        <StatusScreen
          tone="warn"
          title={t('status.expired.title')}
          body={t('status.expired.body')}
        />
      )

    case 'revoked':
      return (
        <StatusScreen
          tone="warn"
          title={t('status.revoked.title')}
          body={t('status.revoked.body')}
        />
      )

    case 'used':
      return (
        <StatusScreen tone="warn" title={t('status.used.title')} body={t('status.used.body')} />
      )

    case 'not_found':
      return (
        <StatusScreen
          tone="danger"
          title={t('error.not_found.title')}
          body={t('error.not_found.body')}
        />
      )

    case 'unusable':
      return (
        <StatusScreen
          tone="warn"
          title={t('pickup.unusable.title')}
          body={t('pickup.unusable.body')}
        />
      )

    case 'error':
      return (
        <StatusScreen tone="danger" title={t('error.title')} body={t(state.messageKey)}>
          {state.retryable ? (
            <Button size="lg" onClick={retry}>
              {t('button.retry')}
            </Button>
          ) : null}
        </StatusScreen>
      )

    case 'session': {
      const { session } = state
      const parties = partiesOf(session.base, session.held, {
        signed: session.signedHere,
        queued: session.queuedHere,
      })
      if (isPickupComplete(parties)) {
        return <PickupDone context={session.base} parties={parties} />
      }

      if (session.view.step === 'handoff') {
        const { role, handoff, failed } = session.view
        return (
          <HandoffPanel
            role={role}
            {...(handoff === undefined ? {} : { handoff })}
            failed={failed}
            onRetry={() => show({ step: 'handoff', role, failed: false })}
            onBack={() => show({ step: 'overview' })}
          />
        )
      }

      // Captured without a connection. The other row still has to be signed, often within the
      // minute and on this same phone, so the notice is a step of the session rather than its end.
      if (session.view.step === 'queued') {
        return (
          <QueuedNotice item={session.view.item}>
            <Button variant="ghost" size="lg" block onClick={() => show({ step: 'overview' })}>
              {t('pickup.queued.back')}
            </Button>
          </QueuedNotice>
        )
      }

      if (session.view.step === 'sign') {
        const { role, busy } = session.view
        const link = partyFor(parties, role).link
        if (link.kind === 'holding') {
          return (
            // Rendered at the same position whether or not it is busy, so React keeps the form
            // state — and therefore the drawn signature — across the round trip.
            <SignForm
              context={link.context}
              busy={busy}
              onBack={() => show({ step: 'overview' })}
              onSubmit={(body) => submit({ token: link.token, context: link.context }, role, body)}
            />
          )
        }
      }

      return (
        <Overview
          session={session}
          parties={parties}
          onSign={(role) => show({ step: 'sign', role, busy: false })}
          onHandoff={(role) => show({ step: 'handoff', role, failed: false })}
        />
      )
    }
  }
}

function Overview({
  session,
  parties,
  onSign,
  onHandoff,
}: {
  readonly session: PickupSession
  readonly parties: readonly PickupParty[]
  readonly onSign: (role: PickupRole) => void
  readonly onHandoff: (role: PickupRole) => void
}) {
  const { t, locale } = useSignI18n()

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-col gap-1">
        <h1 className="font-semibold text-display text-ink">{t('pickup.title')}</h1>
        <p className="text-body text-muted">{t('pickup.intro')}</p>
        <p className="text-caption text-muted">
          {t('sign.expires', { timestamp: formatDateTime(session.base.expires_at, locale) })}
        </p>
      </header>

      {/* Who still has to sign comes before the note itself: that is what the dock is waiting on. */}
      <PickupParties parties={parties} onSign={onSign} onHandoff={onHandoff} />

      <ShipmentSummary context={session.base} />
    </div>
  )
}

/** Sentinel for a link that opened fine but signs a box this session is not about. */
const UNUSABLE = Symbol('not a pickup link')

interface OpenedLinks {
  readonly links: readonly HeldLink[]
  /** Why nothing could be opened, when nothing could. */
  readonly failure?: unknown
}

type OpenResult =
  | { readonly token: string; readonly context: SignContext }
  | { readonly token: string; readonly cause: unknown }

/**
 * Opens every token the URL carries, tolerating the ones that fail. A session whose driver link
 * was already used is still worth opening on the sender's link, and the other way round.
 */
async function openLinks(
  tokens: readonly string[],
  locale: SignLocale,
  signal: AbortSignal,
): Promise<OpenedLinks> {
  const results: readonly OpenResult[] = await Promise.all(
    tokens.map(async (token): Promise<OpenResult> => {
      try {
        return { token, context: await loadSignContext(token, { locale, signal }) }
      } catch (cause) {
        return { token, cause }
      }
    }),
  )

  const links: HeldLink[] = []
  for (const result of results) {
    if (!('context' in result) || !isPickupRole(result.context.role)) continue
    // A handoff token resolves to the link it was made for; that is the one that signs.
    links.push({ token: result.context.token ?? result.token, context: result.context })
  }
  if (links.length > 0) return { links }

  // Nothing usable: either every token failed, or no link signs a box this session is about.
  const failed = results.find((result) => 'cause' in result)
  return { links, failure: failed === undefined ? UNUSABLE : failed.cause }
}

/**
 * The session as the queue leaves it: rows whose capture has been delivered become signatures this
 * session recorded, rows that are still waiting follow their item, and a driver looking at the
 * notice of a capture that has since gone out is put back on the overview, where the row says so.
 *
 * Returns `null` when the queue has nothing new to say, so a publish that concerns another screen
 * never re-renders this one.
 */
function settleQueued(
  session: PickupSession,
  items: readonly QueueItem[],
): { readonly session: PickupSession; readonly delivered: boolean } | null {
  const settled = settleRecorded({ signed: session.signedHere, queued: session.queuedHere }, items)
  if (settled === null) return null

  // A row this session was looking at is done being looked at, however it ended: delivered, or
  // thrown away on the queue screen. Either way the overview is where the answer is.
  const leftQueue =
    session.view.step === 'queued' && settled.recorded.queued?.[session.view.role] === undefined
  return {
    session: {
      ...session,
      signedHere: settled.recorded.signed ?? {},
      queuedHere: settled.recorded.queued ?? {},
      view: leftQueue ? { step: 'overview' } : session.view,
    },
    delivered: settled.delivered.length > 0,
  }
}

/** Re-reads the shipment through a link that is still open, so the rows tell the truth again. */
async function refreshed(
  base: SignContext,
  held: readonly HeldLink[],
  locale: SignLocale,
): Promise<SignContext> {
  const open = held[0]
  if (open === undefined) return base
  try {
    return await loadSignContext(open.token, { locale })
  } catch {
    // The rows this session recorded itself carry the outcome; a stale note is no reason to fail.
    return base
  }
}

/**
 * One beat of the handoff screen. A link that is still ours answers who has signed, name and all;
 * with nothing left to read, the handed-out link itself is asked, and a 409 is the answer — it
 * says the signature landed, but not by whom.
 */
async function poll(
  role: PickupRole,
  locale: SignLocale,
  session: PickupSession | null,
  setSession: (next: PickupSession) => void,
): Promise<void> {
  if (session === null || session.view.step !== 'handoff') return
  const spent = () => session.held.filter((held) => held.context.role !== role)

  const other = session.held.find((held) => held.context.role !== role)
  if (other !== undefined) {
    try {
      const context = await loadSignContext(other.token, { locale })
      const signed = context.signatures.some((signature) => signature.role === role)
      setSession(
        signed
          ? { ...session, base: context, held: spent(), view: { step: 'overview' } }
          : { ...session, base: context },
      )
    } catch {
      // A dropped beat is not news; the next one asks again.
    }
    return
  }

  const mine = session.held.find((held) => held.context.role === role)
  if (mine === undefined) return
  try {
    await loadSignContext(mine.token, { locale })
  } catch (cause) {
    if (cause instanceof ApiError && cause.code === 'already_signed') {
      setSession({
        ...session,
        held: spent(),
        signedHere: { ...session.signedHere, [role]: {} },
        view: { step: 'overview' },
      })
    }
  }
}

/** Maps an API failure onto the screen it deserves; anything unknown is the generic error. */
function failureState(cause: unknown): PickupState {
  if (cause === UNUSABLE) return { kind: 'unusable' }
  if (!(cause instanceof ApiError)) {
    return { kind: 'error', messageKey: 'error.unexpected', retryable: false }
  }
  switch (cause.code) {
    case 'token_revoked':
      return { kind: 'revoked' }
    case 'token_expired':
    case 'link_expired':
    case 'token_rotated':
      return { kind: 'expired' }
    case 'already_signed':
      return { kind: 'used' }
    case 'not_found':
      return { kind: 'not_found' }
    default:
      return { kind: 'error', messageKey: cause.messageKey, retryable: cause.isRetryable }
  }
}
